import { useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@nexus/ui";
import type { OverviewFilters } from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import type { CompetitorFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  COMP_TOPIC_DETAIL_DRAWER_WIDTH,
  type CompTopicDetailContext,
} from "../../lib/compTopicDetailContext.js";
import {
  useCompTopicDetailExampleIndex,
  useCompTopicDetailExamples,
} from "../../hooks/useCompTopicDetailExamples.js";
import { shouldKeepTopicDetailDrawerOpen } from "../TopicDetailDrawer/shouldKeepTopicDetailDrawerOpen.js";
import { CompTopicDetailInfoTab } from "./CompTopicDetailInfoTab.js";
import { CompTopicDetailExamplesTab } from "./CompTopicDetailExamplesTab.js";
import { CompTopicDetailFatigueTab } from "./CompTopicDetailFatigueTab.js";

export type CompTopicDetailTab = "info" | "examples" | "fatigue";

export interface CompTopicDetailDrawerProps {
  tenantId: string;
  context: CompTopicDetailContext | null;
  open: boolean;
  activeTab: CompTopicDetailTab;
  onOpenChange: (open: boolean) => void;
  onActiveTabChange: (tab: CompTopicDetailTab) => void;
  overviewFilters: OverviewFilters;
  competitorFilters: CompetitorFiltersInput;
}

export function CompTopicDetailDrawer({
  tenantId,
  context,
  open,
  activeTab,
  onOpenChange,
  onActiveTabChange,
  overviewFilters,
  competitorFilters,
}: CompTopicDetailDrawerProps) {
  const { t } = useTranslation();
  const examplesScrollRef = useRef<HTMLDivElement>(null);
  const [selectedExampleIndex, setSelectedExampleIndex] = useCompTopicDetailExampleIndex(context);

  const {
    data: examples,
    isLoading: examplesLoading,
    isError: examplesError,
    fetchNextPage: fetchMoreExamples,
    hasNextPage: hasMoreExamples,
    isFetchingNextPage: isFetchingMoreExamples,
    isFetchNextPageError: examplesFetchMoreError,
  } = useCompTopicDetailExamples(
    tenantId,
    overviewFilters,
    competitorFilters,
    context,
    open,
  );

  // Preview navigation can walk past the loaded batches — pull the next one in
  // before the user reaches the end so paging stays seamless.
  useEffect(() => {
    if (
      hasMoreExamples &&
      !isFetchingMoreExamples &&
      !examplesFetchMoreError &&
      selectedExampleIndex >= examples.length - 1
    ) {
      fetchMoreExamples();
    }
  }, [
    selectedExampleIndex,
    examples.length,
    hasMoreExamples,
    isFetchingMoreExamples,
    examplesFetchMoreError,
    fetchMoreExamples,
  ]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        closePosition="left"
        closeIcon="chevron-right"
        className={`flex w-full flex-col overflow-hidden bg-card p-0 ${COMP_TOPIC_DETAIL_DRAWER_WIDTH}`}
        onPointerDownOutside={(e) => {
          if (shouldKeepTopicDetailDrawerOpen(e.target)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (shouldKeepTopicDetailDrawerOpen(e.target)) e.preventDefault();
        }}
      >
        <SheetHeader className="border-b px-6 pb-4 pt-6">
          <div className="pl-10">
            <SheetTitle className="text-base font-medium">
              {t("overview.topicDetail.title", { defaultValue: "Topic Detail" })}
            </SheetTitle>
          </div>
        </SheetHeader>

        {!context ? (
          <p className="px-6 py-5 text-sm text-muted-foreground">
            {t("overview.topicDetail.noContext", { defaultValue: "Topic context is unavailable." })}
          </p>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(v) => onActiveTabChange(v as CompTopicDetailTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="border-b px-6">
              <TabsList className="h-10 w-full justify-start rounded-none border-0 bg-transparent p-0">
                <TabsTrigger value="info" className="rounded-none">
                  {t("overview.topicDetail.tabInfo", { defaultValue: "Info" })}
                </TabsTrigger>
                <TabsTrigger value="examples" className="rounded-none">
                  {t("overview.topicDetail.tabExamples", { defaultValue: "Examples" })}
                </TabsTrigger>
                <TabsTrigger value="fatigue" className="rounded-none">
                  {t("overview.topicDetail.tabFatigue", { defaultValue: "Fatigue" })}
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="info" className="mt-0 flex min-h-0 flex-1 flex-col overflow-y-auto">
              <CompTopicDetailInfoTab
                context={context}
                examples={examples}
                examplesLoading={examplesLoading}
                selectedExampleIndex={selectedExampleIndex}
                onSelectedExampleIndexChange={setSelectedExampleIndex}
              />
            </TabsContent>

            <TabsContent
              value="examples"
              ref={examplesScrollRef}
              className="mt-0 min-h-0 flex-1 overflow-y-auto"
            >
              {activeTab === "examples" && (
                <CompTopicDetailExamplesTab
                  examples={examples}
                  isLoading={examplesLoading}
                  isError={examplesError}
                  hasNextPage={!!hasMoreExamples}
                  isFetchingNextPage={isFetchingMoreExamples}
                  isFetchNextPageError={examplesFetchMoreError}
                  onFetchNextPage={fetchMoreExamples}
                  scrollContainerRef={examplesScrollRef}
                />
              )}
            </TabsContent>

            <TabsContent value="fatigue" className="mt-0 min-h-0 flex-1 overflow-y-auto">
              {activeTab === "fatigue" && (
                <CompTopicDetailFatigueTab
                  tenantId={tenantId}
                  context={context}
                  overviewFilters={overviewFilters}
                  competitorFilters={competitorFilters}
                />
              )}
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
